import { signal, computed } from '@preact/signals';
import type { Message, OptimisticMessage } from '../types';

/**
 * Widget state using Preact signals for reactive updates
 */

// UI state
export const isOpen = signal(false);
export const isOnline = signal(navigator.onLine);

// Auth state
export const token = signal<string | null>(null);
export const tokenExpiresAt = signal<Date | null>(null);
export const conversationId = signal<string | null>(null);

// Messages state
export const messages = signal<Message[]>([]);
export const pendingMessages = signal<Map<string, OptimisticMessage>>(new Map());
export const isLoadingMessages = signal(false);
export const hasMoreMessages = signal(false);
export const nextCursor = signal<string | null>(null);

// Sending state
export const isSending = signal(false);

// Error state
export const error = signal<string | null>(null);

// Computed: all messages including pending
export const allMessages = computed(() => {
  const confirmed = messages.value;
  const pending = Array.from(pendingMessages.value.values());

  // Merge, avoiding duplicates by matching idempotency keys
  const confirmedIds = new Set(confirmed.map((m: Message) => m.id));
  const confirmedKeys = new Set(
    confirmed.map((m: Message) => m.idempotency_key).filter(Boolean)
  );
  const filteredPending = pending.filter(
    (p: OptimisticMessage) =>
      !confirmedIds.has(p.id) &&
      (!p._idempotencyKey || !confirmedKeys.has(p._idempotencyKey))
  );

  return [...confirmed, ...filteredPending].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
});

/**
 * Add an optimistic message while sending
 */
export function addPendingMessage(idempotencyKey: string, text: string): void {
  const msg: OptimisticMessage = {
    id: `pending-${idempotencyKey}`,
    sender_type: 'customer',
    sender_operator_id: null,
    sanitized_text: text,
    created_at: new Date().toISOString(),
    _status: 'sending',
    _idempotencyKey: idempotencyKey,
  };

  const current = new Map(pendingMessages.value);
  current.set(idempotencyKey, msg);
  pendingMessages.value = current;
}

/**
 * Mark pending message as failed
 */
export function markPendingFailed(idempotencyKey: string): void {
  const current = new Map(pendingMessages.value);
  const msg = current.get(idempotencyKey);
  if (msg) {
    current.set(idempotencyKey, { ...msg, _status: 'failed' });
    pendingMessages.value = current;
  }
}

/**
 * Remove pending message (e.g., after confirmed)
 */
export function removePendingMessage(idempotencyKey: string): void {
  const current = new Map(pendingMessages.value);
  current.delete(idempotencyKey);
  pendingMessages.value = current;
}

/**
 * Add confirmed message from API response
 */
export function addConfirmedMessage(message: Message): void {
  // Check for duplicates
  if (messages.value.some((m: Message) => m.id === message.id)) {
    return;
  }

  messages.value = [...messages.value, message].sort(
    (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  );
}

/**
 * Set messages from API (replace all)
 */
export function setMessages(newMessages: Message[], hasMore: boolean, cursor: string | null): void {
  messages.value = newMessages;
  hasMoreMessages.value = hasMore;
  nextCursor.value = cursor;
}

/**
 * Merge new messages from polling (avoid duplicates)
 */
export function mergeMessages(newMessages: Message[]): void {
  const existingIds = new Set(messages.value.map((m: Message) => m.id));
  const uniqueNew = newMessages.filter((m: Message) => !existingIds.has(m.id));

  if (uniqueNew.length > 0) {
    messages.value = [...messages.value, ...uniqueNew].sort(
      (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }
}

/**
 * Reset all state
 */
export function resetState(): void {
  isOpen.value = false;
  token.value = null;
  tokenExpiresAt.value = null;
  conversationId.value = null;
  messages.value = [];
  pendingMessages.value = new Map();
  isLoadingMessages.value = false;
  hasMoreMessages.value = false;
  nextCursor.value = null;
  isSending.value = false;
  error.value = null;
}

/**
 * Listen for online/offline events
 */
export function initOnlineListener(): () => void {
  const handleOnline = () => {
    isOnline.value = true;
    error.value = null; // Clear network errors
  };

  const handleOffline = () => {
    isOnline.value = false;
    error.value = 'You are offline. Messages will be sent when you reconnect.';
  };

  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  // Initial state
  isOnline.value = navigator.onLine;

  return () => {
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}
